import useData from 'DesktopMessager/useData'
import './TopBar.scoped.scss'
import useAct from 'DesktopMessager/useAct'
import YesNoModal from 'Shared/components/YesNoModal'
import ButtonWithCheckMark from 'Shared/components/ButtonWithCheckMark'
import BackButton from 'Shared/components/BackButton'
import goalActions from 'Shared/components/GoalButton/actions'
import ServerWaitButton from 'Shared/components/ServerWaitButtonStandalone'
import Tooltip from "react-simple-tooltip"

export default function TopBar({ title, noMoreMessages = false }) {
  const act = useAct()
  const targetId = useData(rs => rs.target.id)
  const campaignTargetId = useData(rs => rs.conversation.campaignTargetId)
  const { name: targetName } = useSel(s => s.target || {})

  const goals = useData(rs => rs.campaign.effort?.goals || [])
  const effort = useData(rs => rs.campaign.effort || {})

  const [optOutOnDone, setOptOutOnDone] = useState(false)
  const [goalComplete, setGoalComplete] = useState(null)
  const [selectedGoalId, setSelectedGoalId] = useState(null)
  const [showGoalDropdown, setShowGoalDropdown] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    if (!targetId) return
    setOptOutOnDone(false)
    if (selectedGoalId) {
      setSelectedGoalId(null)
      goalActions.getGoalComplete(campaignTargetId, selectedGoalId)
        .then((complete) => setGoalComplete)
    }
  }, [targetId])

  const [showGoToNextTargetModal, setShowGoToNextTargetModal] = useState(false)

  const currentConversation = useData(s => s.conversation)
  const textMessages = useSel(s =>
    _.values(s.textMessages.entities).filter(
      tm => tm.conversationId === currentConversation?.id
    )
  )
  const lastMessageDirection = _.orderBy(textMessages, 'id', 'desc')[0]?.direction

  const handleAcceptModal = async () => {
    return goToNext().then(() => setShowGoToNextTargetModal(false))
  }

  const handleGoalChange = (goalId) => {
    setSelectedGoalId(goalId)
    setShowGoalDropdown(false)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowGoalDropdown(false)
      }
    }
    if (showGoalDropdown) document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showGoalDropdown])

  const handleDoneClick = async () => {
    if (lastMessageDirection === 'outbound') {
      return goToNext()
    } else {
      return Promise.resolve().then(() => setShowGoToNextTargetModal(true))
    }
  }

  const handleSnoozeClick = async () => {
    return act.conversationManager.snooze()
  }

  const goToNext = async () => {
    const preActions = []

    if (selectedGoalId !== null) {
      preActions.push(goalActions.markGoalComplete(campaignTargetId, selectedGoalId))
    }
    if (optOutOnDone) {
      preActions.push(act.conversationManager.optOut())
    }

    // Run pre-actions in parallel, then goToNext
    if (preActions.length > 0) {
      await Promise.all(preActions)
    }

    return act.conversationManager.goToNext()
  }

  return <>
    <YesNoModal
      title={i18n.t('shared.messages.are_you_sure')}
      body={i18n.t('user.campaigns.text.conversation_needs_reply')}
      show={showGoToNextTargetModal}
      onAccept={handleAcceptModal}
      onDecline={() => Promise.resolve().then(() => setShowGoToNextTargetModal(false))}
      useServerWaitButtons={true}
    />

    <div className={`top-bar ${noMoreMessages ? 'no-more-messages' : ''}`}>
      <BackButton onClick={() => window.history.back()} />
      <h1>{title}</h1>

      {!noMoreMessages && <>
        <div className="target">
          <div className="icon">
            <SvgIconsSms width={20} height={20} />
          </div>
          <h1>{targetName}</h1>
        </div>

        <div className="h-divider" />

        <div className="options">
          {goals?.length === 1 && goals[0]?.name && (
            <ButtonWithCheckMark
              tick
              onClick={() =>
                !selectedGoalId
                  ? handleGoalChange(goals[0].id)
                  : setSelectedGoalId(null)
              }
              checked={selectedGoalId !== null}
              disabled={goalComplete}
            >
              {goals[0].name}
            </ButtonWithCheckMark>
          )}
          {goals?.length > 1 && (
            <div className="sub-goal-selector-container">
              {goals &&
                <ButtonWithCheckMark
                  tick
                  onClick={() => {
                    setShowGoalDropdown(true),
                      setSelectedGoalId(null)
                  }}
                  checked={selectedGoalId !== null}
                  disabled={goalComplete}
                >
                  {selectedGoalId ? goals.find(g => g.id === selectedGoalId)?.name : effort.name}
                </ButtonWithCheckMark>}
              <div className="subgoal-menu" ref={dropdownRef}>
                {showGoalDropdown && (
                  <div className="popup popup-list narrow">
                    <div className="group">
                      {goals.map((goal) => (
                        <div
                          key={goal.id}
                          className={`filter bold ${selectedGoalId === goal.id ? 'selected' : ''}`}
                          onClick={() => handleGoalChange(goal.id)}
                        >
                          {goal.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <ButtonWithCheckMark cross onClick={() => setOptOutOnDone(!optOutOnDone)} checked={optOutOnDone}>
            {i18n.t('user.campaigns.text.opt_out')}
          </ButtonWithCheckMark>
        </div>

        <div className="h-divider" />

        <Tooltip
          content="Snooze temporarily pauses this conversation and moves it to later in your queue. Use this when you want to come back to this contact after handling other conversations."
          placement="bottom"
          radius={8}
          background="#333"
          color="#fff"
          border="#666"
          fontSize={12}
          padding={8}
        >
          <div className="info-container">
            <SvgIconsCircleInfo />
          </div>
        </Tooltip>

        <div className="snooze">
          <ServerWaitButton secondary onClick={handleSnoozeClick}>{i18n.t('user.campaigns.text.snooze')}</ServerWaitButton>
        </div>

        <div className="done">
          <ServerWaitButton primary darken onClick={handleDoneClick}>{i18n.t('user.campaigns.text.done_go_next')}</ServerWaitButton>
        </div>
      </>}
    </div>
  </>
}

